import type { useT } from '../i18n';
import { fmtDist, type ProxState, type SensorView } from './sensors';
import type { LidarView } from './lidar';

export interface RadarLayers {
  depth: boolean;
  pres: boolean;
  tracks: boolean;
  /** camera people + the camera FOV wedge (missing in layer settings saved before it existed = on) */
  cam?: boolean;
  /** §27 live lidar 360° (missing = on) */
  lidar?: boolean;
}
export const DEFAULT_LAYERS: RadarLayers = { depth: true, pres: true, tracks: true, cam: true, lidar: true };

export const STATE_COLOR: Record<ProxState, string> = {
  normal: '#4CB782',
  approaching: '#F0A04B',
  tooClose: '#E5534B',
  cliff: '#E5534B',
  nodata: '#9BA1A8',
  charging: '#5AA9E6',
};
export const bandColor = (d: number) => (d < 0.5 ? '#E5534B' : d < 1 ? '#F0A04B' : '#2BB3A3');

export function stateLabel(t: ReturnType<typeof useT>, v: SensorView, units: 'm' | 'cm') {
  const pplAppr = v.approaching.filter((x) => x.kind === 'person').length;
  if (v.state === 'charging') return t('sens.capCharging');
  if (v.state === 'nodata' && !v.people?.active) return v.has ? t('sens.capStale') : t('sens.capNoSensors');
  if (v.people?.active && v.people.count > 0 && v.state !== 'cliff')
    return t('sens.capPeople', { p: v.people.count, n: pplAppr, d: v.nearest !== null ? fmtDist(v.nearest, units, 1) : '—' });
  if (v.state === 'cliff') return t('sens.capCliff');
  return t('sens.capSummary', { n: v.approaching.length, d: v.nearest !== null ? fmtDist(v.nearest, units, 1) : '—' });
}

/** One-line summary of the lidar layer (mini-radar caption, aria label). */
export function lidarCaption(t: ReturnType<typeof useT>, l: LidarView, units: 'm' | 'cm') {
  if (l.status !== 'live' && l.status !== 'moving') return `${t('lidar.layer')} · ${t(`lidar.badge.${l.status}`, { hz: '' })}`;
  if (!l.points.length) return t('lidar.captionNone');
  let near = Infinity;
  for (const [, r] of l.points) if (r < near) near = r;
  return t('lidar.caption', { d: fmtDist(Math.max(0, near - l.bodyM), units, 1) });
}
